/**
 * Which address a message is sent from (ADR 0099).
 *
 * A mailbox may send as its own address or as one of its aliases
 * (`Account.aliases`, the server's list). The server checks every From against
 * that list itself; these helpers only decide what the composer offers and
 * starts on, so a refused From is a bug here, never a way round the check.
 */
import type { Address, MessageHeader } from '@/api/types';

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Every address the account receives at: its own first, then its aliases. */
export function ownAddresses(email: string | undefined, aliases: readonly string[] = []): string[] {
  const out: string[] = [];
  for (const address of [email, ...aliases]) {
    if (address && !out.some((seen) => same(seen, address))) out.push(address);
  }
  return out;
}

/**
 * The From a composer sends with: `chosen` spelled as the account spells it,
 * when it is one of `choices`; otherwise the first choice (the mailbox).
 *
 * With no choices yet -- the accounts list still loading -- `chosen` is kept
 * as it is rather than replaced: the server refuses one that is not allowed,
 * and swapping a restored alias for the mailbox on a slow load would send it
 * from the wrong address without a word.
 */
export function pickFrom(choices: readonly string[], chosen: string | undefined): string | undefined {
  if (choices.length === 0) return chosen;
  if (chosen !== undefined) {
    const match = choices.find((choice) => same(choice, chosen));
    if (match !== undefined) return match;
  }
  return choices[0];
}

/**
 * The alias a message reached this mailbox at, for the From of a reply: the
 * first of the mailbox's own addresses found in To, then Cc, then the
 * Delivered-To / X-Original-To trace a Bcc'd copy carries. Undefined when the
 * account has no aliases or none of its addresses is there (the composer then
 * sends from the mailbox). The mailbox's own address counts as a match, so a
 * message sent to both answers from whichever came first.
 */
export function deliveredTo(
  message: { to: readonly Address[]; cc?: readonly Address[]; headers?: readonly MessageHeader[] },
  email: string | undefined,
  aliases: readonly string[],
): string | undefined {
  if (aliases.length === 0) return undefined;
  const mine = ownAddresses(email, aliases);
  const traced = (message.headers ?? [])
    .filter((header) => /^(delivered-to|x-original-to)$/i.test(header.name))
    .map((header) => ({ email: header.value.replace(/^.*<|>.*$/g, '').trim() }));
  for (const candidate of [...message.to, ...(message.cc ?? []), ...traced]) {
    const match = mine.find((own) => same(own, candidate.email));
    if (match !== undefined) return match;
  }
  return undefined;
}
